import React, { useCallback } from 'react';
import { Table, Button, Input, InputNumber, Select, Radio, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FN110, FN1121 } from '../../../services/n202Api';
import LookupField from '../../../components/LookupField';

interface Tab2Props {
  formData: Partial<FN110>;
  rows: Partial<FN1121>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FN1121>[]) => Partial<FN1121>[]) => void;
  onVendorFieldChange: (key: keyof FN110, value: FN110[keyof FN110]) => void;
  isFieldDisabled: (key: string) => boolean;
}

const Tab2Vendor1Details: React.FC<Tab2Props> = ({
  formData, rows, isEditing, onRowsChange, onVendorFieldChange, isFieldDisabled,
}) => {
  const handleCellChange = useCallback((idx: number, key: keyof FN1121, value: FN1121[keyof FN1121]) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }, [onRowsChange]);

  const handleAdd = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fn1121_02 || 0), 0);
    onRowsChange(prev => [...prev, { fn1121_01: formData.fn110_01, fn1121_02: maxSeq + 1 }]);
  }, [rows, formData.fn110_01, onRowsChange]);

  const handleDelete = useCallback((idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn1121_02: i + 1 })));
  }, [onRowsChange]);

  const columns: ColumnsType<Partial<FN1121>> = [
    { title: <span data-contract-field="fn1121_02">#</span>, dataIndex: 'fn1121_02', width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn1121_11">原因代碼</span>, dataIndex: 'fn1121_11', width: 120,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn1121_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn1121_12">幣別</span>, dataIndex: 'fn1121_12',
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn1121_12', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn1121_13">匯率</span>, dataIndex: 'fn1121_13',
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={value => handleCellChange(i, 'fn1121_13', value || 0)} style={{ width: '100%' }} />
        : v,
    },
    {
      title: <span data-contract-field="fn1121_14">金額</span>, dataIndex: 'fn1121_14', width: 120, align: 'right' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn1121_14', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    ...(isEditing ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN1121>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleDelete(i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* Vendor 1 header fields */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto 1fr)', gap: '8px 12px', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>廠商代號</span>
        <LookupField lookupType="vendor" value={formData.fn110_41 || ''} onChange={v => onVendorFieldChange('fn110_41', v)} disabled={isFieldDisabled('fn110_41')} maxLength={12} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>幣別</span>
        <LookupField lookupType="currency" value={formData.fn110_42 || ''} onChange={v => onVendorFieldChange('fn110_42', v)} disabled={isFieldDisabled('fn110_42')} maxLength={3} style={{ maxWidth: 100 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>匯率</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_43')} value={formData.fn110_43 || 0} onChange={v => onVendorFieldChange('fn110_43', v || 0)} style={{ maxWidth: 100 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>採購人員</span>
        <LookupField lookupType="employee" value={formData.fn110_49 || ''} onChange={v => onVendorFieldChange('fn110_49', v)} disabled={isFieldDisabled('fn110_49')} maxLength={12} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>貨款抵扣</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_44')} value={formData.fn110_44 || 0} onChange={v => onVendorFieldChange('fn110_44', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>抵扣類型</span>
        <Select size="small" disabled={isFieldDisabled('fn110_451')} value={formData.fn110_451 || undefined} onChange={v => onVendorFieldChange('fn110_451', v)} style={{ maxWidth: 160 }} allowClear
          options={[{ value: '1', label: '庫位移轉單' }, { value: '2', label: '出庫通知單' }, { value: '3', label: '客訴買收交運單' }, { value: '4', label: '無單據' }]} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>進貨退款</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_45')} value={formData.fn110_45 || 0} onChange={v => onVendorFieldChange('fn110_45', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>退款</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_46')} value={formData.fn110_46 || 0} onChange={v => onVendorFieldChange('fn110_46', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>買收單號</span>
        <Input size="small" disabled={isFieldDisabled('fn110_452')} value={formData.fn110_452 || ''} onChange={e => onVendorFieldChange('fn110_452', e.target.value)} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>繳庫退回類別</span>
        <Radio.Group disabled={isFieldDisabled('fn110_461')} value={formData.fn110_461 || undefined} onChange={e => onVendorFieldChange('fn110_461', e.target.value)} size="small">
          <Radio value="1">繳庫退回單</Radio>
          <Radio value="2">出庫通知單</Radio>
        </Radio.Group>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>退款參考單號</span>
        <Input size="small" disabled={isFieldDisabled('fn110_462')} value={formData.fn110_462 || ''} onChange={e => onVendorFieldChange('fn110_462', e.target.value)} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>廠商備註</span>
        <Input.TextArea size="small" disabled={isFieldDisabled('fn110_47')} value={formData.fn110_47 || ''} onChange={e => onVendorFieldChange('fn110_47', e.target.value)} rows={2} style={{ gridColumn: 'span 3', maxWidth: 400 }} />
      </div>

      {/* Complaint reasons table */}
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增原因</Button>
        </Space>
      )}
      <Table<Partial<FN1121>>
        columns={columns}
        dataSource={rows}
        rowKey={(_r, i) => String(i)}
        size="small"
        pagination={false}
        bordered
        scroll={{ y: 300 }}
      />
    </div>
  );
};

export default React.memo(Tab2Vendor1Details);
